import { applySnapshot, flow, types } from "mobx-state-tree";
import { ErrorModel } from "../../components/common/error";
import { allOptionCode } from "../../components/project/documentobjective/ProjectTypeDropdownList";
import { IInput } from "../../utils/common-interface";
import customtypes from "../../utils/mobx-types-helper";
import { ProjectTypeLoanSummaryModel } from "../project/ProjectModel";
import { CurrencyModel, PaginationModel, SelectListModel } from "../share";
import {
    ActivityPlanModel,
    ActivityPlanSummaryModel,
    IActivityPlanModel,
    IAmountObject,
    SignLoanMap,
} from "../share/ActivityModel";
import {
    ILoanSourcesModel,
    ILoanTypeModel,
    LoanPeriodsModel,
    LoanSourcesModel,
    LoanTypeModel,
} from "../share/LoanModel";
import LogsAPI from "./LogService";
import { FileModel, IFileModel } from "../../components/fileupload/FileModel";


export const LogNewDebtPlanDetailsModel = types
    .model("LogNewDebtPlanDetailsModel", {
        planProjType: customtypes.optional(types.string, ""),
        planID: customtypes.optional(types.number, 0),
        projectID: customtypes.optional(types.number, 0),
        organization: types.optional(SelectListModel, {}),
        projectCode: customtypes.optional(types.string, ""),
        projectTHName: customtypes.optional(types.string, ""),
        isNewProject: customtypes.optional(types.boolean, false),
        pdmoAgreement: customtypes.optional(types.number, 0),
        fromFiveYearPlan: types.optional(LoanPeriodsModel, {}),
        yearPlanSummary: customtypes.optional(LoanPeriodsModel, {}),
        activityPlans: types.optional(types.array(ActivityPlanModel), []),
        activityPlanSummary: customtypes.optional(ActivityPlanSummaryModel, {}),
        isNotRequiredApproval: customtypes.optional(types.boolean, false),
        projectType: customtypes.optional(types.string, ""),
        projectTypeLabel: customtypes.optional(types.string, ""),
        loanTypeLabel: customtypes.optional(types.string, ""),
        gfCode: customtypes.optional(types.string, ""),
        masterAgreement: customtypes.optional(types.string, ""),
    })
    .views((self: any) => ({
        get projectTHNameView() {
            return self.projectTHName || "-";
        },
        get newDebtPlanDetailsJSON() {
            return self.toJSON();
        },
        get totalLocalSignLoanAsMillionBaht() {
            return self.activityPlans.reduce(
                (sum: number, activityPlan: IActivityPlanModel, index: number) => {

                    let subSum = 0;

                    if (activityPlan.loanProcessPlan && activityPlan.loanProcessPlan.length > 0) {
                        const loanProcessArray = Array.from(activityPlan.loanProcessPlan);
                        const loanProcessSum = loanProcessArray.reduce(
                            (subTotal: number, plan: any, planIndex: number) => {
                                // Only count local loans (sourceType "L")
                                if (plan.loanSource && plan.loanSource.sourceType === "L") {
                                    const amount = typeof plan.signedLoanSumAmount === "number" ? plan.signedLoanSumAmount : 0;
                                    return subTotal + amount;
                                } else {
                                    return subTotal;
                                }
                            },
                            0
                        );
                        subSum += loanProcessSum;
                    }

                    if (activityPlan.loanTypePlans && activityPlan.loanTypePlans.length > 0) {
                        const loanTypeArray = Array.from(activityPlan.loanTypePlans);
                        const loanTypeSum = loanTypeArray.reduce(
                            (subTotal: number, loanTypePlan: any, loanTypeIndex: number) => {

                                if (loanTypePlan.loanSourcePlans && loanTypePlan.loanSourcePlans.length > 0) {
                                    const loanSourceArray = Array.from(loanTypePlan.loanSourcePlans);
                                    const localLoanAmount = loanSourceArray.reduce(
                                        (localSum: number, loanSource: any, sourceIndex: number) => {

                                            // Only count local loans (sourceType "L" and currency "THB")
                                            if (loanSource.sourceType === "L" &&
                                                (loanSource.currency === "THB" || !loanSource.currency)) {
                                                const amount = typeof loanSource.loanAmount === "number" ? loanSource.loanAmount : 0;
                                                return localSum + amount;
                                            } else {
                                            }
                                            return localSum;
                                        },
                                        0
                                    );
                                    return subTotal + localLoanAmount;
                                }
                                return subTotal;
                            },
                            0
                        );
                        subSum += loanTypeSum;
                    }
                    const newSum = sum + subSum / 1000000;
                    return newSum;
                },
                0
            );
        },
        get foreignSignLoanAsMillionBaht() {
            const foreignLoans = new Map<string, { amountAsMillion: number }>();

            self.activityPlans.forEach((activityPlan: IActivityPlanModel) => {
                // Check loanProcessPlan first (original structure)
                if (activityPlan.loanProcessPlan && activityPlan.loanProcessPlan.length > 0) {
                    const loanProcessArray = Array.from(activityPlan.loanProcessPlan);
                    loanProcessArray.forEach((plan: any) => {
                        if (plan.loanSource &&
                            plan.loanSource.sourceType === "F" &&
                            plan.loanSource.currency &&
                            plan.loanSource.currency !== "THB") {
                            const currency = plan.loanSource.currency;
                            const amount = typeof plan.signedLoanSumAmount === "number"
                                ? plan.signedLoanSumAmount / 1000000
                                : 0;

                            if (foreignLoans.has(currency)) {
                                foreignLoans.get(currency)!.amountAsMillion += amount;
                            } else {
                                foreignLoans.set(currency, { amountAsMillion: amount });
                            }
                        }
                    });
                }

                // Check loanTypePlans for new structure
                if (activityPlan.loanTypePlans && activityPlan.loanTypePlans.length > 0) {
                    const loanTypeArray = Array.from(activityPlan.loanTypePlans);
                    loanTypeArray.forEach((loanTypePlan: any) => {
                        if (loanTypePlan.loanSourcePlans && loanTypePlan.loanSourcePlans.length > 0) {
                            const loanSourceArray = Array.from(loanTypePlan.loanSourcePlans);
                            loanSourceArray.forEach((loanSource: any) => {
                                // Only count foreign loans (sourceType "F" and non-THB currency)
                                if (loanSource.sourceType === "F" &&
                                    loanSource.currency &&
                                    loanSource.currency !== "THB") {
                                    const currency = loanSource.currency;
                                    const amount = typeof loanSource.loanAmount === "number"
                                        ? loanSource.loanAmount / 1000000
                                        : 0;

                                    if (foreignLoans.has(currency)) {
                                        foreignLoans.get(currency)!.amountAsMillion += amount;
                                    } else {
                                        foreignLoans.set(currency, { amountAsMillion: amount });
                                    }
                                }
                            });
                        }
                    });
                }
            });

            return foreignLoans;
        },

        get totalOtherSignLoanAsMillionBaht() {
            return self.activityPlans.reduce(
                (sum: number, activityPlan: IActivityPlanModel) => {
                    let subSum = 0;

                    // Check loanProcessPlan first (original structure)
                    if (activityPlan.loanProcessPlan && activityPlan.loanProcessPlan.length > 0) {
                        const loanProcessArray = Array.from(activityPlan.loanProcessPlan);
                        subSum += loanProcessArray.reduce(
                            (subTotal: number, plan: any) => {
                                if (plan.loanSource && plan.loanSource.sourceType === "O") {
                                    return subTotal + (typeof plan.signedLoanSumAmount === "number"
                                        ? plan.signedLoanSumAmount
                                        : 0);
                                }
                                return subTotal;
                            },
                            0
                        );
                    }

                    // Check loanTypePlans for new structure
                    if (activityPlan.loanTypePlans && activityPlan.loanTypePlans.length > 0) {
                        const loanTypeArray = Array.from(activityPlan.loanTypePlans);
                        subSum += loanTypeArray.reduce(
                            (subTotal: number, loanTypePlan: any) => {
                                if (loanTypePlan.loanSourcePlans && loanTypePlan.loanSourcePlans.length > 0) {
                                    const loanSourceArray = Array.from(loanTypePlan.loanSourcePlans);
                                    const otherLoanAmount = loanSourceArray.reduce(
                                        (otherSum: number, loanSource: any) => {
                                            // Only count other loans (sourceType "O")
                                            if (loanSource.sourceType === "O") {
                                                return otherSum + (typeof loanSource.loanAmount === "number"
                                                    ? loanSource.loanAmount
                                                    : 0);
                                            }
                                            return otherSum;
                                        },
                                        0
                                    );
                                    return subTotal + otherLoanAmount;
                                }
                                return subTotal;
                            },
                            0
                        );
                    }

                    // Convert to millions
                    return sum + subSum / 1000000;
                },
                0
            );
        },
        get foreignSignLoanSummaryListGroupByActivityPlans() {
            const totalSignLoanMap: SignLoanMap = new Map();
            self.activityPlans.forEach((activityPlan: IActivityPlanModel) => {
                activityPlan.foreignSignLoanSummaryList.forEach(
                    (amount: IAmountObject, currency: string) => {
                        const loanPlanInMap = totalSignLoanMap.get(currency);
                        totalSignLoanMap.set(currency, {
                            sourceType: amount.sourceType,
                            currencyCode: amount.currencyCode,
                            amount:
                                (loanPlanInMap ? loanPlanInMap.amount : 0) + amount.amount,
                            amountAsMillion:
                                (loanPlanInMap ? loanPlanInMap.amountAsMillion : 0) +
                                amount.amountAsMillion,
                        });
                    },
                );
            });
            return totalSignLoanMap;
        },
    }))
    .actions((self: any) => ({
        setAllField: (data: any) => {
            Object.keys(data).forEach((key) => {
                try {
                    self[key] = data[key];
                } catch (e) {
                    console.log("exception on mapping key:", key);
                }
            });
        },
        setField: ({ fieldname, value }: IInput) => {
            self[fieldname] = value;
        },
        setProjectType: (text: string, value: any) => {
            self.projectType = value;
            self.projectTypeLabel = text;
        },
    }));
export type ILogNewDebtPlanDetailsModel = typeof LogNewDebtPlanDetailsModel.Type;

export const LogNewDebtPlanSummaryModel = types
    .model("LogNewDebtPlanSummaryModel", {
        description: customtypes.optional(types.string, ""),
        overAllByLoanSource: customtypes.optional(
            types.array(LoanSourcesModel),
            [],
        ),
        overAllByLoanType: customtypes.optional(types.array(LoanTypeModel), []),
        overAllByProjectType: customtypes.optional(
            types.array(ProjectTypeLoanSummaryModel),
            [],
        ),
        currencyData: types.maybeNull(CurrencyModel),
    })
    .views((self: any) => ({
        //
    }))
    .actions((self: any) => ({
        overAllBySelectedLoanSource(
            sourceType: "A" | "L" | "F" | "O",
        ): ILoanSourcesModel {
            const loansource = self.overAllByLoanSource.find(
                (loanSouce: ILoanSourcesModel) => loanSouce.sourceType === sourceType,
            );
            return loansource || LoanSourcesModel.create({ sourceType });
        },
        overAllBySelectedLoanType(selectedType: "D" | "G" | "MF"): ILoanTypeModel {
            const loansource = self.overAllByLoanType.find(
                (loanType: ILoanTypeModel) => loanType.selectedType === selectedType,
            );
            return loansource || LoanTypeModel.create({ selectedType });
        },
        setField: ({ fieldname, value }: IInput) => {
            self[fieldname] = value;
        },
    }));
export type ILogNewDebtPlanSummaryModel = typeof LogNewDebtPlanSummaryModel.Type;

export const LogNewDebtPlanModel = types
    .model("LogNewDebtPlanModel", {
        planSummary: customtypes.optional(LogNewDebtPlanSummaryModel, {}),
        planDetails: customtypes.optional(types.array(LogNewDebtPlanDetailsModel), []),
        files: customtypes.optional(types.array(FileModel), []),
        error: types.optional(ErrorModel, {}),
        loading: types.optional(types.boolean, false),
        projectType: types.optional(types.string, allOptionCode),
        projectTypeLabel: types.maybe(types.string),
        selectedProjectId: types.maybe(types.number),
        searchText: types.optional(types.string, ""),
        selectedProjectIdArray: types.optional(types.array(types.number), []),
        projectTypeForArray: types.optional(types.string, allOptionCode),
        projectTypeLabelForArray: types.maybe(types.string),
    })
    .views((self: any) => ({
        get planDetailsByFilter(): ILogNewDebtPlanDetailsModel[] {
            return self.planDetails.filter((plan: ILogNewDebtPlanDetailsModel) => {
                if (
                    (plan.projectTHName.includes(self.searchText) ||
                        plan.projectCode.includes(self.searchText)) &&
                    (self.projectType === plan.projectType ||
                        self.projectType === allOptionCode)
                ) {
                    return true;
                } else {
                    return false;
                }
            });
        },
        get planDetailsJSON() {
            const { planDetails, ...rest } = self.toJSON();
            return planDetails;
        },
        // เพิ่มฟังก์ชัน getLoanAmountTypeA เข้าไปใน views
        get getLoanAmountTypeA() {
            // ตรวจสอบว่า newDebtPlanSummary และ overAllByLoanSource มีการโหลดแล้วหรือไม่
            if (
                !self.planSummary ||
                !self.planSummary.overAllByLoanSource
            ) {
                return 0;
            }

            // ค้นหาข้อมูลเงินกู้ที่มี sourceType เป็น "A" ด้วยวิธีที่ยืดหยุ่นมากขึ้น
            const loanTypeA = self.planSummary.overAllByLoanSource.find(
                (loan: any) => {
                    if (!loan || typeof loan.sourceType !== "string") return false;
                    return loan.sourceType.trim() === "A";
                }
            );

            // ถ้าไม่พบ log ออกมาและ return ค่าที่กำหนด
            if (!loanTypeA) {
                return 2; // ค่าที่คุณต้องการให้ return เมื่อไม่พบข้อมูล
            }

            // ดำเนินการต่อหากพบข้อมูล
            if (
                !self.planSummary.currencyData ||
                !self.planSummary.currencyData.currency
            ) {
                return loanTypeA.loanAmount;
            }

            const currencyObj = self.planSummary.currencyData.currency.find(
                (curr: any) => curr.currencyCode === loanTypeA.currency
            );

            const currencyRate = currencyObj ? currencyObj.currencyRate : 1.0;

            // คำนวณผลลัพธ์
            return (loanTypeA.loanAmount * currencyRate) / 1000000;
        },

        get loanSourceSummaryByProjectType() {
            // สร้าง summaryMap พร้อมทั้ง 2 categories ตั้งแต่ต้น
            const summaryMap: Record<string, { name: string; เงินกู้ในประเทศ: number; เงินกู้ต่างประเทศ: number }> = {
                "กู้เพื่อลงทุน": {
                    name: "กู้เพื่อลงทุน",
                    เงินกู้ในประเทศ: 0,
                    เงินกู้ต่างประเทศ: 0,
                },
                "กู้เพื่อดำเนินโครงการหรือสภาพคล่อง": {
                    name: "กู้เพื่อดำเนินโครงการหรือสภาพคล่อง",
                    เงินกู้ในประเทศ: 0,
                    เงินกู้ต่างประเทศ: 0,
                }
            };

            // รวมข้อมูลจาก planDetails
            (self.planDetails || []).forEach((detail: any) => {
                // กำหนดชื่อ category ตาม projectType
                let name = "กู้เพื่อดำเนินโครงการหรือสภาพคล่อง";
                if ((detail.projectType || "").trim() === "DEV") {
                    name = "กู้เพื่อลงทุน";
                }

                // รวมข้อมูลจาก activityPlanSummary.loanSourcePlans
                const loanSourcePlans = detail.activityPlanSummary!.loanSourcePlans || [];

                loanSourcePlans.forEach((loanSourcePlan: any) => {
                    const loanSource = loanSourcePlan.loanSource;
                    if (loanSource) {
                        const thbAmount = loanSource.thbAmount || 0;
                        const sourceType = loanSource.sourceType;

                        // คำนวณเป็นล้านบาท
                        const amountInMillion = thbAmount / 1000000;

                        // จำแนกตามแหล่งเงิน
                        if (sourceType === "L") {
                            summaryMap[name].เงินกู้ในประเทศ += amountInMillion;
                        } else if (sourceType === "F") {
                            summaryMap[name].เงินกู้ต่างประเทศ += amountInMillion;
                        }
                    }
                });
            });

            // แปลง Object เป็น Array ก่อนส่งคืน (จะมีทั้ง 2 categories เสมอ)
            return Object.values(summaryMap);
        }

    }))
    .actions((self: any) => ({
        getNewDebtPlanLogDetail: flow(function* (id: number) {
            try {
                self.loading = true;
                const result = yield LogsAPI.getNewDebtPlanLogDetail(
                    id,
                );
                self.setAllField(result.data);
                self.planSummary = result.data.planSummary;
                self.planDetails = result.data.planDetails;
            } catch (e) {
                self.error.tigger = true;
                self.error.code = e.code;
                self.error.title = e.name;
                self.error.message = e.message;
                self.error.technicalStack = e.technicalStack;
                throw e;
            } finally {
                self.loading = false;
            }
        }),
        // setPlanId: (id: number) => {
        //     self.planHeader.planID = id;
        // },
        setStartYear: (planYearTxt: string) => {
            self.planYearTxt = planYearTxt;
            self.planYear = parseInt(planYearTxt.substr(0, 4), 10);
        },
        setField: ({ fieldname, value }: IInput) => {
            self[fieldname] = value;
        },
        setProjectType: (text: string, value: any) => {
            self.projectType = value;
            self.projectTypeLabel = text;
        },
        resetAll: () => {
            applySnapshot(self, {});
        },
        setAllField: (data: any) => {
            Object.keys(data).forEach((key) => {
                try {
                    self[key] = data[key];
                } catch (e) {
                    console.log("exception on mapping key:", key);
                }
            });
        },
        onAddAttatchFiles: (file: File[]) => {
            file.forEach((_file: File) => {
                try {
                    const newFileUpload: IFileModel = FileModel.create({});
                    newFileUpload.setToBase64Format(_file);
                    self.files.push(newFileUpload);
                } catch (e) {
                    console.log("onAddDebtsFile Error: ", e);
                }
            });
        },
        onRemoveAttatchFile: (file: IFileModel) => {
            const index = self.files.findIndex(
                (_file: IFileModel) => _file.fileName === file.fileName
            );
            if (index >= 0) {
                self.files.splice(index, 1);
            }
        },
        // ฟังก์ชันสำหรับจัดการ Array
        addSelectedProjectId: (projectId: number) => {
            if (!self.selectedProjectIdArray.includes(projectId)) {
                self.selectedProjectIdArray.push(projectId);
            }
        },
        removeSelectedProjectId: (projectId: number) => {
            const index = self.selectedProjectIdArray.indexOf(projectId);
            if (index > -1) {
                self.selectedProjectIdArray.splice(index, 1);
            }
        },
        clearSelectedProjectIdArray: () => {
            self.selectedProjectIdArray = [];
        },
    }));
export type ILogNewDebtPlanModel = typeof LogNewDebtPlanModel.Type;
